import { FcmBootstrap } from "@/shared/notifications/components/FcmBootstrap";
import { RealtimeNotice } from "@/shared/components/RealtimeNotice";
import { useAccessRealtime } from "@/shared/services/useAccessRealtime";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { AppShell } from "@/shared/layout/AppShell";
import { Surface } from "@/shared/layout/Surface";
import { getLocaleDirection } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "./locale";
import { useSessionQuery } from "@/shared/services/useSessionQuery";

export function ProtectedLayout() {
  const locale = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations();
  const { data, isLoading, error, refetch } = useSessionQuery();
  const { status: realtimeStatus, reportCredentialError } = useAccessRealtime(data?.admin?.id);

  useEffect(() => {
    if (
      error instanceof ApiJsonClientError &&
      (error.status === 401 || error.status === 403)
    ) {
      navigate(`/${locale}/admin/login`, { replace: true });
    }
  }, [error, locale, navigate]);

  if (
    error instanceof ApiJsonClientError &&
    (error.status === 401 || error.status === 403)
  ) {
    return <Navigate to={`/${locale}/admin/login`} replace />;
  }

  if (isLoading) {
    return (
      <Surface
        locale={locale}
        direction={getLocaleDirection(locale)}
        label={t(keys.common.loading)}
      />
    );
  }

  return (
    <>
    <FcmBootstrap adminId={data?.admin?.id} firebaseToken={data?.firebaseToken} onCredentialError={reportCredentialError} />
    <RealtimeNotice status={realtimeStatus} onRetry={() => {
      if (realtimeStatus === "unconfigured" || !data?.firebaseToken) void refetch({ cancelRefetch: false });
    }} />
    <AppShell
      locale={locale}
      isSuperAdmin={data?.admin?.roles?.includes("SUPER_ADMIN")}
      adminId={data?.admin?.id}
      adminName={data?.admin?.name}
      adminRoles={data?.admin?.roles}
    >
      {/*
        Only the routed page is keyed on the pathname, so a page's local state
        (forms, filters, a record id reused by the same route) starts fresh on
        every navigation. The shell chrome — and the notification bell's
        Firestore listener inside it — stays mounted across navigations.
      */}
      <Outlet key={location.pathname} />
    </AppShell>
    </>
  );
}
